import { useMutation, useQuery } from "@tanstack/react-query";
import rg4js from "raygun4js";
import { useEffect, useState } from "react";
import { Container } from "react-bootstrap";
import { useHistory, useParams } from "react-router-dom";
import Loading from "@/components/Loading";
import { toast } from "@/components/ui/toast";
import { useDocumentApi } from "@/hooks/useApi";
import { useDocumentFormInterrogatories } from "@/hooks/useDocumentFormInterrogatories";
import { getDocumentOptions } from "@/lib/query-options";
import type { DocumentDetailClient } from "@/lib/types";
import { addUploadDate, normalizeError } from "@/lib/utils";
import type { FormIntLi } from "./FormInterrogatoriesList";
import FormInterrogatoriesSelect from "./FormInterrogatoriesSelect";

export default function UpdateFormInterrogatories() {
  const { documentId } = useParams<{ documentId: string }>();
  const documentApi = useDocumentApi();

  const documentQuery = useQuery({
    ...getDocumentOptions(documentId, documentApi),
    enabled: !!documentId,
    select: (data) => addUploadDate(data) as DocumentDetailClient,
  });
  const clearMissingFormInterrogatoryMutation = useMutation({
    mutationFn: () =>
      documentApi.documentClearMissingFormInterrogatory({
        documentId: documentId,
      }),
    onError: (error) => {
      toast.error("Failed to clear missing form interrogatory");
      rg4js("send", {
        error: normalizeError(error),
      });
    },
    onSettled: () => {
      documentQuery.refetch();
    },
  });
  const history = useHistory();
  const formInterrogatories = useDocumentFormInterrogatories(
    documentQuery.data?.info?.documentSubType,
  );

  const [formIntData, setFormIntData] = useState<FormIntLi[]>([]);

  //set selected and highlighted values for form int data
  // biome-ignore lint/correctness/useExhaustiveDependencies: watch setFormIntData
  useEffect(() => {
    const relevantQuestions = (
      formInterrogatories?.filter(
        (frog) => frog.form === documentQuery.data?.info?.formIntType,
      ) ?? []
    ).map((rq) => {
      let idx = documentQuery.data?.interrogatories?.findIndex(
        (rog) => rog.text === rq.id,
      );
      if (idx !== -1) {
        (rq as FormIntLi).selected = true;
      }
      idx = documentQuery.data?.info?.missingFormInts?.indexOf(rq.id ?? "");
      if (idx !== -1 && documentQuery.data?.info?.missingFormInts) {
        (rq as FormIntLi).highlighted = true;
      }
      return rq;
    });
    setFormIntData(relevantQuestions);
  }, [documentQuery.data, formInterrogatories, setFormIntData]);

  //clear missing form ints if no highlighted values remain
  // biome-ignore lint/correctness/useExhaustiveDependencies: watch clearMissingFormInterrogatoryMutation.mutate
  useEffect(() => {
    if (
      formIntData.filter((q) => (q as FormIntLi).highlighted).length <= 0 &&
      formIntData.length > 0
    ) {
      clearMissingFormInterrogatoryMutation.mutate();
    }
  }, [formIntData]);

  // go to document wizard if no missing form ints
  useEffect(() => {
    if (
      documentQuery.data &&
      (documentQuery.data?.info?.missingFormInts?.length ?? 0) <= 0
    ) {
      history.push(`/wizard/${documentQuery.data?.id}/0`);
    }
  }, [documentQuery.data, history]);

  return (
    <Loading className="mt-3" isLoading={documentQuery.isLoading}>
      <Container className="page-container">
        <FormInterrogatoriesSelect
          formIntData={formIntData}
          setFormIntData={setFormIntData}
        />
      </Container>
    </Loading>
  );
}
